import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Key, ArrowRight, Send, Loader2, Building, Mail, User, MessageSquare, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { apiRequest } from "@/lib/queryClient";

type ModalView = "enter-key" | "request-access";

interface GetAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialAccessKey?: string;
  /**
   * Marketing pages pass this so the dialog stays key-entry only: signups
   * belong to the waitlist form, whose state-of-record is Customer.io. Without
   * it the dialog keeps its legacy "Request Access" view (HubSpot).
   */
  onJoinWaitlist?: () => void;
}

const accessKeySchema = z.object({
  accessKey: z
    .string()
    .min(1, "Access key is required")
    .regex(
      /^BRAIN-[A-Z0-9]{4}-[A-Z0-9]{4}$/,
      "Invalid key format. Expected format: BRAIN-XXXX-XXXX"
    ),
});

type AccessKeyFormData = z.infer<typeof accessKeySchema>;

interface ValidateKeyResponse {
  valid?: boolean;
  success?: boolean;
  message?: string;
}

function formatAccessKey(value: string): string {
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  
  if (cleaned.length === 0) return "";
  
  if (cleaned.length <= 5) {
    if (cleaned.startsWith("BRAIN".slice(0, cleaned.length))) {
      return cleaned;
    }
    return "BRAIN".slice(0, Math.max(5, cleaned.length));
  }
  
  const prefix = "BRAIN";
  const rest = cleaned.slice(5);
  
  if (rest.length <= 4) {
    return `${prefix}-${rest}`;
  }
  
  const firstGroup = rest.slice(0, 4);
  const secondGroup = rest.slice(4, 8);
  
  if (secondGroup.length > 0) {
    return `${prefix}-${firstGroup}-${secondGroup}`;
  }
  
  return `${prefix}-${firstGroup}`;
}

function EnterAccessKeyView({
  onSwitchToRequest,
  initialAccessKey,
  onJoinWaitlist,
}: {
  /** Omitted on the marketing pages, where the waitlist form owns signups. */
  onSwitchToRequest?: () => void;
  initialAccessKey?: string;
  onJoinWaitlist?: () => void;
}) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AccessKeyFormData>({
    resolver: zodResolver(accessKeySchema),
    defaultValues: {
      accessKey: initialAccessKey ? formatAccessKey(initialAccessKey) : "",
    },
  });

  const [serverError, setServerError] = useState<string | null>(null);
  const accessKeyValue = watch("accessKey");

  const validateMutation = useMutation({
    mutationFn: async (data: { accessKey: string }) => {
      return await apiRequest("/api/begin-access-login", {
        method: "POST",
        body: JSON.stringify(data),
      }) as ValidateKeyResponse & { redirectTo?: string };
    },
    onSuccess: (response) => {
      if (response.success && response.redirectTo) {
        window.location.href = response.redirectTo;
      } else if (!response.success) {
        setServerError(response.message || "Invalid access key");
      }
    },
    onError: (error: Error) => {
      setServerError(error.message || "Failed to validate access key");
    },
  });

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatAccessKey(e.target.value);
    setValue("accessKey", formatted, { shouldValidate: false });
  };

  const onSubmit = (data: AccessKeyFormData) => {
    setServerError(null);
    validateMutation.mutate({ accessKey: data.accessKey });
  };

  return (
    <div className="p-6 space-y-6" data-testid="view-enter-key">
      <div className="space-y-2">
        <h2
          className="text-2xl font-bold text-foreground"
          data-testid="text-enter-key-title"
        >
          Enter Access Key
        </h2>
        <p className="text-muted-foreground text-sm">
          Enter your access key to get started with BrainWrk
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="accessKey">Access Key</Label>
          <div className="relative">
            <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="accessKey"
              type="text"
              placeholder="BRAIN-XXXX-XXXX"
              {...register("accessKey")}
              onChange={handleKeyChange}
              className="pl-10 font-mono uppercase"
              maxLength={15}
              data-testid="input-access-key"
            />
          </div>
          {errors.accessKey && (
            <p
              className="text-sm text-destructive"
              data-testid="text-key-validation-error"
            >
              {errors.accessKey.message}
            </p>
          )}
        </div>

        {serverError && (
          <div
            className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
            data-testid="text-server-error"
          >
            {serverError}
          </div>
        )}

        <Button
          type="submit"
          disabled={validateMutation.isPending}
          className="w-full h-12 text-base bg-landing-mint text-[#1a1a1a] hover:bg-landing-mint/90 border-2 border-[#1a1a1a]"
          data-testid="button-continue"
        >
          {validateMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Validating...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Don't have an access key?{" "}
          <button
            type="button"
            onClick={onJoinWaitlist ?? onSwitchToRequest}
            className="text-landing-mint hover:underline font-medium hover-elevate active-elevate-2 px-1 rounded"
            data-testid="link-request-access"
          >
            {onJoinWaitlist ? "Join the waitlist" : "Request Access"}
          </button>
        </p>
      </div>
    </div>
  );
}

const requestAccessSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().optional(),
  message: z.string().optional(),
});

type RequestAccessFormData = z.infer<typeof requestAccessSchema>;

interface RequestAccessResponse {
  success?: boolean;
  message?: string;
}

function RequestAccessView({
  onSwitchToEnterKey,
}: {
  onSwitchToEnterKey: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestAccessFormData>({
    resolver: zodResolver(requestAccessSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      message: "",
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data: RequestAccessFormData) => {
      return await apiRequest("/api/request-access", {
        method: "POST",
        body: JSON.stringify(data),
      }) as RequestAccessResponse;
    },
    onSuccess: (response) => {
      if (response.success) {
        setSubmitted(true);
      } else {
        setServerError(response.message || "Failed to submit request");
      }
    },
    onError: (error: Error) => {
      setServerError(error.message || "Failed to submit request");
    },
  });

  const onSubmit = (data: RequestAccessFormData) => {
    setServerError(null);
    submitMutation.mutate(data);
  };

  if (submitted) {
    return (
      <div className="p-6 space-y-6" data-testid="view-request-success">
        <div className="text-center space-y-4 py-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-landing-mint/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-landing-mint" />
          </div>
          <h2 className="text-2xl font-bold text-foreground" data-testid="text-success-title">
            Request Submitted!
          </h2>
          <p className="text-muted-foreground text-sm max-w-xs mx-auto">
            Thanks for your interest in BrainWrk. We'll review your request and get back to you soon.
          </p>
        </div>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Already have an access key?{" "}
            <button
              type="button"
              onClick={onSwitchToEnterKey}
              className="text-landing-mint hover:underline font-medium hover-elevate active-elevate-2 px-1 rounded"
              data-testid="link-enter-key-success"
            >
              Enter Access Key
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="view-request-access">
      <div className="space-y-2">
        <h2
          className="text-2xl font-bold text-foreground"
          data-testid="text-request-access-title"
        >
          Request Access
        </h2>
        <p className="text-muted-foreground text-sm">
          Fill out the form below and we'll get back to you
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                {...register("firstName")}
                className="pl-10"
                data-testid="input-first-name"
              />
            </div>
            {errors.firstName && (
              <p className="text-sm text-destructive">{errors.firstName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                {...register("lastName")}
                className="pl-10"
                data-testid="input-last-name"
              />
            </div>
            {errors.lastName && (
              <p className="text-sm text-destructive">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="john@company.com"
              {...register("email")}
              className="pl-10"
              data-testid="input-email"
            />
          </div>
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">Company (optional)</Label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="company"
              type="text"
              placeholder="Acme Inc."
              {...register("company")}
              className="pl-10"
              data-testid="input-company"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Message (optional)</Label>
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Textarea
              id="message"
              placeholder="Tell us a bit about your team and training needs..."
              {...register("message")}
              className="pl-10 min-h-[80px] resize-none"
              data-testid="input-message"
            />
          </div>
        </div>

        {serverError && (
          <div
            className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
            data-testid="text-server-error"
          >
            {serverError}
          </div>
        )}

        <Button
          type="submit"
          disabled={submitMutation.isPending}
          className="w-full h-12 text-base bg-landing-mint text-[#1a1a1a] hover:bg-landing-mint/90 border-2 border-[#1a1a1a]"
          data-testid="button-submit-request"
        >
          {submitMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Request
            </>
          )}
        </Button>
      </form>

      <div className="text-center border-t pt-4">
        <p className="text-sm text-muted-foreground">
          Already have an access key?{" "}
          <button
            type="button"
            onClick={onSwitchToEnterKey}
            className="text-landing-mint hover:underline font-medium hover-elevate active-elevate-2 px-1 rounded"
            data-testid="link-enter-key"
          >
            Enter Access Key
          </button>
        </p>
      </div>
    </div>
  );
}

export function GetAccessModal({
  open,
  onOpenChange,
  initialAccessKey,
  onJoinWaitlist,
}: GetAccessModalProps) {
  const [view, setView] = useState<ModalView>("enter-key");

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        setView("enter-key");
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 overflow-hidden"
        aria-describedby={undefined}
        data-testid="modal-get-access"
      >
        <VisuallyHidden>
          <DialogTitle>Get Access to BrainWrk</DialogTitle>
        </VisuallyHidden>
        <div className="transition-all duration-300 ease-in-out">
          {view === "enter-key" && (
            <EnterAccessKeyView
              onSwitchToRequest={onJoinWaitlist ? undefined : () => setView("request-access")}
              initialAccessKey={initialAccessKey}
              onJoinWaitlist={
                onJoinWaitlist
                  ? () => {
                      handleOpenChange(false);
                      onJoinWaitlist();
                    }
                  : undefined
              }
            />
          )}
          {view === "request-access" && (
            <RequestAccessView
              onSwitchToEnterKey={() => setView("enter-key")}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
